import { useEffect, useState } from "react";
import { InputSearch } from "../../components/InputSearch";
import {
    Chart as ChartJS,
    ArcElement,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
    PointElement,
    LineElement,
    LineController,
} from "chart.js";
import { TreemapController, TreemapElement } from "chartjs-chart-treemap";
import { Link } from "react-router-dom";
import { TypeChart } from "./components/TypeChart";
import { ProcedureChart } from "./components/ProcedureChart";
import { ContractorChart } from "./components/ContractorChart";
import { WonTendersChart } from "./components/WonTendersChart";
import { TendersPublishedChart } from "./components/TendersPublishedChart";
import { TendersApplyChart } from "./components/TendersApplyChart";
import { TendersRegionChart } from "./components/TendersRegion";
import { ExpectedTendersChart } from "./components/ExpectedTendersChart";
import { CPVChart } from "./components/CPVChart";
import { Select } from "../../components/Select";
import { API, Auth, graphqlOperation, Storage } from "aws-amplify";
import { StripeStatus } from "../../routes/config";
import { useSubscription } from "../../context/SubscriptionContext";
import moment from "moment";
import Lottie from "lottie-react";
import chartAnimation from "../../lottie/chart_main.json";
import useAnalyticsEventTracker from "../../tracker/EventTracker";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import clsx from "clsx";
import { RiBoxingLine } from "react-icons/ri";

const Competitor = () => {
    const [loading, setLoading] = useState<boolean>(true);
    const [filterLoading, setFilterLoading] = useState<boolean>(true);
    const [checkbox, setCheckbox] = useState<boolean>(false);
    const { stripeStatus, isLoading: isStatusLoading } = useSubscription();
    const [competitors, setCompetitors] = useState<number>(0);
    const [european, setEuropean] = useState<number>(0);
    const [national, setNational] = useState<number>(0);
    const [data, setData] = useState<any[]>([]);
    const [cleanData, setCleanData] = useState<any[]>([]);
    const [loadingProgress, setLoadingProgress] = useState<number>(0);
    const [currentLoadingYear, setCurrentLoadingYear] = useState<number | null>(null);
    const [loadingMessage, setLoadingMessage] = useState<string>("");

    // Filter data
    const [competitorName, setCompetitorName] = useState<string>("");
    const [contractingAuthorityName, setContractingAuthorityName] =
        useState<string>("");
    const [years, setYears] = useState<string>("");
    const [type, setType] = useState<string>("");
    const [procedure, setProcedure] = useState<string>("");
    const [nature, setNature] = useState<string>("");
    const [cpv, setCpv] = useState<string>("");
    const [cpvList, setCpvList] = useState<any[]>([]);

    ChartJS.register(
        CategoryScale,
        LinearScale,
        BarElement,
        Title,
        Tooltip,
        ArcElement,
        Legend,
        PointElement,
        LineElement,
        LineController,
        TreemapController,
        TreemapElement
    );

    async function filters(newData: any, cpvs: any) {
        setFilterLoading(true);

        const phaseList: any[] = newData.length > 0 ? newData : cleanData;
        const cpvsList: any[] = cpvs.length > 0 ? cpvs : cpvList;

        //prettier-ignore
        const filteredData = phaseList
            .filter((phases: any) => competitorName !== "" ? (phases["Officiële benaming"] ?? '').toString().toLowerCase().includes(competitorName.toLowerCase()) : true)
            .filter((phases: any) => contractingAuthorityName !== "" ? (phases["Naam Aanbestedende dienst"] ?? '').toString().toLowerCase().includes(contractingAuthorityName.toLowerCase()) : true)
            .filter((phases: any) => cpv !== "" ? phases["Hoofd cpv definitie code"]?.includes(cpv) || (phases["Hoofd cpv definitie omschrijving"] ?? '').toString().toLowerCase().includes(cpv.toLowerCase()) : true)
            .filter((phases: any) => years !== "" ? phases["Publicatiedatum"].includes(years) : true)
            .filter((phases: any) => type !== "" ? phases["Type opdracht"].includes(type) : true)
            .filter((phases: any) => procedure !== "" ? phases["Procedure"]?.includes(procedure) : true)
            .filter((phases: any) => nature !== "" ? phases["Aard van de opdracht"].includes(nature) : true)
            .filter((phases: any) => checkbox ? cpvsList.some((cpv: any) => phases["Hoofd cpv definitie code"].substring(0,3) === cpv.cpv.code.substring(0,3)) : true)

        // Filter Europees of Nationaal
        const europeanData = await Promise.all(
            filteredData.filter((phase: any) => {
                return phase["Nationaal of Europees"].includes("Europees");
            })
        );
        setEuropean(europeanData.length);

        const nationalData = await Promise.all(
            filteredData.filter((phase: any) => {
                return phase["Nationaal of Europees"].includes("Nationaal");
            })
        );
        setNational(nationalData.length);

        setCompetitors(filteredData.length);

        setData(filteredData);

        setTimeout(() => {
            setFilterLoading(false);
        }, 1000);
    }

    useEffect(() => {
        document.title = "Concurrentie Analyse | TenderTracer";
        getData();
        EventTracker();
    }, []);

    function EventTracker() {
        useAnalyticsEventTracker(
            "Concurrentieanalyse",
            "Bekijkt Concurrentieanalyse",
            "Concurrentieanalyse"
        );
    }

    useEffect(() => {
        if (!loading) {
            filters([], []);
        }
    }, [years, type, procedure, nature, checkbox]);

    useEffect(() => {
        if (!loading) {
            setFilterLoading(true);
            setTimeout(() => {
                filters([], []);
            }, 3000);
        }
    }, [contractingAuthorityName, competitorName, cpv]);

    async function getData() {
        try {
            setLoading(true);
            setLoadingProgress(0);

            // Load and process files one by one to reduce memory usage
            const years = Array.from({ length: 5 }, (_, i) => 2020 + i); // Will generate 2020, 2021, 2022, 2023, 2024
            let combinedData = [];
            
            // First phase: Loading data
            const totalSteps = years.length * 2; // Loading + Processing for each year
            let currentStep = 0;
            
            for (let i = 0; i < years.length; i++) {
                const year = years[i];
                setCurrentLoadingYear(year);
                setLoadingMessage(`Data laden voor ${year}...`);
                
                try {
                    // Fetch data from S3
                    const response: any = await Storage.get(`data_${year}.json`, {
                        bucket: 'competitor-data-tendertracernl',
                        region: 'eu-west-3',
                        download: true,
                        customPrefix: {
                            public: ''
                        }
                    });

                    currentStep++;
                    setLoadingProgress((currentStep / totalSteps) * 100);

                    if (!response || !response.Body) {
                        console.warn(`Geen data gevonden voor ${year}`);
                        continue;
                    }

                    setLoadingMessage(`Data verwerken voor ${year}...`);
                    // Parse the response data
                    let yearData;
                    if (response.Body instanceof Blob) {
                        const text = await response.Body.text();
                        yearData = JSON.parse(text);
                    } else {
                        console.warn(`Unexpected response format for year ${year}`);
                        continue;
                    }
                    
                    // Filter awarded tenders immediately to reduce memory usage
                    const filteredYearData = yearData.filter((phase: any) => 
                        phase["Publicatie type"].includes("gegunde")
                    );
                    
                    combinedData.push(...filteredYearData);
                    currentStep++;
                    setLoadingProgress((currentStep / totalSteps) * 100);
                } catch (error) {
                    console.error(`Error loading data for year ${year}:`, error);
                    currentStep += 2; // Skip both loading and processing steps on error
                    setLoadingProgress((currentStep / totalSteps) * 100);
                }
            }

            setLoadingMessage("Data analyseren...");
            // Filter Europees of Nationaal
            const europeanCount = combinedData.filter((phase: any) => 
                phase["Nationaal of Europees"].includes("Europees")
            ).length;
            setEuropean(europeanCount);

            const nationalCount = combinedData.filter((phase: any) => 
                phase["Nationaal of Europees"].includes("Nationaal")
            ).length;
            setNational(nationalCount);

            // Sort by publication date (most recent first)
            combinedData.sort((a: any, b: any) => {
                const dateA = new Date(a.Publicatiedatum).valueOf();
                const dateB = new Date(b.Publicatiedatum).valueOf();
                
                // If either date is invalid, treat it as older
                if (Number.isNaN(dateA)) return 1;
                if (Number.isNaN(dateB)) return -1;
                
                return dateB - dateA; // Reverse sort (most recent first)
            });

            setCompetitors(combinedData.length);
            setCleanData(combinedData);

            // Get user's CPV codes
            const { attributes } = await Auth.currentAuthenticatedUser({
                bypassCache: true,
            });

            try {
                const cpvData: any = await API.graphql(graphqlOperation(`
                    query MyQuery { 
                        getUser(id: "${attributes.sub}") { 
                            active 
                            companies { 
                                items { 
                                    company { 
                                        cpv(limit: 99999) { 
                                            items { 
                                                cpv { 
                                                    code 
                                                    description 
                                                } 
                                            } 
                                        } 
                                    } 
                                } 
                            } 
                        } 
                    }
                `));
                
                const company = cpvData.data.getUser.companies.items[0]?.company;
                if (company) {
                    setCpvList(company.cpv.items);
                    
                    await filters(combinedData, company.cpv.items);
                }
            } catch (err: any) {
                console.error("Error fetching CPV data:", err);
                if (err.data?.listContractors?.items) {
                    const contractors = err.data.listContractors.items.filter(Boolean);
                    setCompetitors(contractors);
                }
            }
        } catch (error) {
            console.error("Error loading data:", error);
        } finally {
            setLoading(false);
            setLoadingProgress(100);
            setCurrentLoadingYear(null);
            setLoadingMessage("");
        }
    }

    const colorPalette = [
        "#00aaaa",
        "#FF4D72",
        "#284F82",
        "#F2C80F",
        "#FD625E",
        "#5F6B6D",
        "#FE9666",
        "#00aaaa",
        "#FF4D72",
        "#284F82",
        "#F2C80F",
        "#FD625E",
        "#5F6B6D",
        "#FE9666",
        "#00aaaa",
        "#FF4D72",
        "#284F82",
        "#F2C80F",
        "#FD625E",
        "#5F6B6D",
        "#FE9666",
    ];

    // Lijn voor Doughnut chart
    const doughnutLabelsLine: any = {
        id: "doughnutLabelsLine",
        afterDraw(chart: any, args: any, options: any) {
            //prettier-ignore
            const { ctx, data, chartArea: { top, bottom, left, right, width, height } } = chart;

            data.datasets[0].data.forEach((datapoint: any, index: any) => {
                //prettier-ignore
                const { x, y } = chart.getDatasetMeta(0).data[index].tooltipPosition();

                const halfWidth = width / 2;
                const halfHeight = height / 2;

                const xLine = x >= halfWidth ? x + 25 : x - 25;
                const yLine = y >= halfHeight ? y + 25 : y - 25;
                const extraLine = x >= halfWidth ? 15 : -15;

                if (index < 3) {
                    ctx.beginPath();
                    ctx.moveTo(x, y);
                    ctx.lineTo(xLine, yLine);
                    ctx.lineTo(xLine + extraLine, yLine);
                    ctx.strokeStyle = "black";
                    ctx.stroke();

                    const textWidth = ctx.measureText(
                        chart.data.labels[index].width
                    );
                    const textXPosition = x >= halfWidth ? "left" : "right";
                    const plusFivePx = x >= halfWidth ? 5 : -5;
                    ctx.textAlign = textXPosition;
                    ctx.font = "10px";
                    ctx.textBaseline = "middle";
                    ctx.fillText(
                        chart.data.labels[index].length > 14
                            ? chart.data.labels[index].substring(0, 11) + "..."
                            : chart.data.labels[index],
                        xLine + extraLine + plusFivePx,
                        yLine
                    );
                }
            });
        },
    };

    return (
        <div className="h-screen flex flex-col overflow-hidden">
            <div className="bg-white shadow border border shadow rounded-lg mx-5 mt-5 mb-3 flex items-center p-2">
                <RiBoxingLine className="text-2xl text-primary-blue" />
                <p className="font-bold pl-2 text-lg">
                    Concurrentie Analyse
                </p>
            </div>

            {loading && data.length === 0 ? (
                <div className="w-full flex justify-center items-center py-8">
                    <div className="max-w-[70%] flex flex-col gap-4">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <div className="animate-spin h-6 w-6">
                                    <svg className="text-blue-600" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                    </svg>
                                </div>
                                <span className="text-lg font-medium">
                                    {loadingMessage || 'Data laden...'}
                                </span>
                            </div>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-3">
                            <div 
                                className="bg-blue-600 h-3 rounded-full transition-all duration-300 ease-in-out"
                                style={{ width: `${loadingProgress}%` }}
                            />
                        </div>
                    </div>
                </div>
            ) : (
                <div className="flex-1 overflow-y-auto mx-5 pb-10">
                    <div className="grid grid-cols-3 gap-3 mb-5">
                        <div className="p-2 bg-white shadow border rounded-lg">
                            <p className="font-bold text-sm">
                                Naam concurrerend bedrijf
                            </p>
                            <InputSearch
                                placeholder="Voer hier de naam van je concurrent in..."
                                searchString={(string) =>
                                    setCompetitorName(string)
                                }
                                string={competitorName}
                            />
                        </div>
                        <div className="p-2 bg-white shadow border rounded-lg">
                            <p className="font-bold text-sm">
                                Naam aanbestedende dienst
                            </p>
                            <InputSearch
                                placeholder="Voer hier de naam van de aanbestedende dienst in..."
                                searchString={(string) =>
                                    setContractingAuthorityName(string)
                                }
                                string={contractingAuthorityName}
                            />
                        </div>
                        <div className="p-2 bg-white shadow border rounded-lg row-span-2">
                            <div className="flex">
                                <p className="font-bold w-full text-sm">
                                    Hoofd CPV definitie
                                </p>
                                <div className="group relative flex w-full justify-end">
                                    <p className="text-xs opacity-50 text-right cursor-pointer hover:text-primary-pink">
                                        Meer informatie*
                                    </p>
                                    <span className="absolute top-5 z-20 scale-0 rounded bg-gray-800 p-2 text-xs text-white group-hover:scale-100">
                                        ✨ Wij hebben aan de hand van jouw
                                        deelselectie de voor jou relevante CPV
                                        codes al automatisch aangevinkt. Filter
                                        op alle concurrenten door het vinkje weg
                                        te halen.
                                    </span>
                                </div>
                            </div>
                            <div className="z-10">
                                <InputSearch
                                    placeholder="Voer hier een CPV code in..."
                                    searchString={(string) => setCpv(string)}
                                    string={cpv}
                                />
                            </div>
                            <div
                                className={clsx(
                                    "group flex relative items-center pt-5 pl-1 cursor-pointer",
                                    stripeStatus !== 'premium' &&
                                        "opacity-50 cursor-not-allowed"
                                )}
                                onClick={() =>
                                    stripeStatus === 'premium' ? setCheckbox(!checkbox) : ""
                                }
                            >
                                <input
                                    style={{
                                        width: "1.5vh",
                                        height: "1.5vh",
                                    }}
                                    type="checkbox"
                                    checked={checkbox}
                                    className={
                                        stripeStatus !== 'premium' ? "cursor-not-allowed" : ""
                                    }
                                    onChange={() => ""}
                                />
                                <p className={clsx("pl-2")}>
                                    Filteren binnen mijn deelselectie
                                </p>
                                {stripeStatus !== 'premium' && (
                                    <span className="absolute top-30 left-0 w-full justify-center items-center scale-0 rounded bg-gray-800 p-2 text-xs text-white group-hover:scale-100 flex">
                                        <FontAwesomeIcon
                                            className="mr-3 drop-shadow-xl animate-bounce"
                                            color="#ffd700"
                                            icon="crown"
                                        />
                                        <p className="font-bold">Premium</p>
                                    </span>
                                )}
                            </div>
                        </div>
                        <div className="h-10 w-full grid grid-cols-4 gap-4 col-span-2">
                            <div className="w-full h-full bg-white shadow border rounded-lg">
                                <Select
                                    onChange={(event) => {
                                        setYears(
                                            event.currentTarget.value.toString()
                                        );
                                    }}
                                >
                                    <option value="">Alle jaren</option>
                                    <option value="2020">2020</option>
                                    <option value="2021">2021</option>
                                    <option value="2022">2022</option>
                                    <option value="2023">2023</option>
                                    <option value="2024">2024</option>
                                </Select>
                            </div>
                            <div className="w-full h-full bg-white shadow border rounded-lg">
                                <Select
                                    onChange={(event) => {
                                        setType(
                                            event.currentTarget.value.toString()
                                        );
                                    }}
                                >
                                    <option value="">
                                        Alle type opdrachten
                                    </option>
                                    <option value="Diensten">Diensten</option>
                                    <option value="Leveringen">
                                        Leveringen
                                    </option>
                                    <option value="Werken">Werken</option>
                                </Select>
                            </div>
                            <div className="w-full h-full bg-white shadow border rounded-lg">
                                <Select
                                    onChange={(event) => {
                                        setProcedure(
                                            event.currentTarget.value.toString()
                                        );
                                    }}
                                >
                                    <option value="">Alle procedures</option>
                                    <option value="Openbaar">Openbaar</option>
                                    <option value="Niet-openbaar">
                                        Niet-openbaar
                                    </option>
                                    <option value="Onderhandeling zonder bekendmaking">
                                        Onderhandeling zonder bekendmaking
                                    </option>
                                    <option value="Mededelingsprocedure met onderhandeling">
                                        Mededelingsprocedure met onderhandeling
                                    </option>
                                    <option value="Onderhandse procedure">
                                        Onderhandse procedure
                                    </option>
                                    <option value="Concurrentiegerichte dialoog">
                                        Concurrentiegerichte dialoog
                                    </option>
                                    <option value="Innovatiepartnerschap">
                                        Innovatiepartnerschap
                                    </option>
                                </Select>
                            </div>
                            <div className="w-full h-full bg-white shadow border rounded-lg">
                                <Select
                                    onChange={(event) => {
                                        setNature(
                                            event.currentTarget.value.toString()
                                        );
                                    }}
                                >
                                    <option value="">
                                        Alle aard van opdrachten
                                    </option>
                                    <option value="Overheidsopdracht">
                                        Overheidsopdracht
                                    </option>
                                    <option value="Raamovereenkomst">
                                        Raamovereenkomst
                                    </option>
                                    <option value="Instellen van dynamisch aankoopsysteem">
                                        Instellen van dynamisch aankoopsysteem
                                        (DAS)
                                    </option>
                                    <option value="Opdracht">Opdracht</option>
                                </Select>
                            </div>
                        </div>
                    </div>

                    {filterLoading ? (
                        <div className="w-full flex justify-center">
                            <Lottie
                                animationData={chartAnimation}
                                className="drop-shadow-2xl"
                            />
                        </div>
                    ) : (
                        <div className="grid grid-cols-4 gap-3 px-5">
                            <div className="grid grid-cols-3 gap-3">
                                <div className="bg-white shadow border pl-3 py-3 row-span-1 rounded-lg flex flex-col">
                                    <p className="opacity-50 text-sm">
                                        Aantal Gunningen
                                    </p>
                                    <p className="text-[1.7vw] pt-3">
                                        {competitors}
                                    </p>
                                </div>
                                <div className="bg-white shadow border pl-3 py-3 row-span-1 rounded-lg flex flex-col">
                                    <p className="opacity-50 text-sm pr-2">
                                        Aantal Nationaal
                                    </p>
                                    <p className="text-[1.7vw] pt-3">
                                        {national}
                                    </p>
                                </div>
                                <div className="bg-white shadow border pl-3 py-3 row-span-1 rounded-lg flex flex-col">
                                    <p className="opacity-50 text-sm pr-2">
                                        Aantal Europees
                                    </p>
                                    <p className="text-[1.7vw] pt-3">
                                        {european}
                                    </p>
                                </div>
                            </div>
                            <div className="bg-white shadow border rounded-lg row-span-2 flex col-span-1 flex-col py-3">
                                <p className="opacity-50 text-sm pb-3 pl-3">
                                    Aard van opdrachten
                                </p>
                                <div className="relative w-full flex items-center justify-center">
                                    <ContractorChart
                                        data={data}
                                        colorPalette={colorPalette}
                                        doughnutLabelsLine={doughnutLabelsLine}
                                    />
                                </div>
                            </div>
                            <div className="bg-white shadow border rounded-lg row-span-2 flex col-span-1 flex-col py-3">
                                <p className="opacity-50 text-sm pb-3 pl-3">
                                    Aantal procedures
                                </p>
                                <div className="relative w-full flex items-center justify-center">
                                    <ProcedureChart
                                        data={data}
                                        colorPalette={colorPalette}
                                        doughnutLabelsLine={doughnutLabelsLine}
                                    />
                                </div>
                            </div>
                            <div className="bg-white shadow border rounded-lg row-span-2 flex col-span-1 flex-col py-3">
                                <p className="opacity-50 text-sm pb-3 pl-5">
                                    Type opdrachten
                                </p>
                                <div className="relative w-full flex items-center justify-center">
                                    <TypeChart
                                        data={data}
                                        colorPalette={colorPalette}
                                        doughnutLabelsLine={doughnutLabelsLine}
                                    />
                                </div>
                            </div>
                            <div className="bg-white shadow border row-span-5 relative rounded-lg flex items-center flex-col overflow-y-auto">
                                <p className="opacity-50 pt-5 pb-3">
                                    Top 10 aflopende contracten {competitorName}
                                </p>
                                <div
                                    className={clsx(
                                        "w-full h-full",
                                        stripeStatus !== 'premium' && "blur"
                                    )}
                                >
                                    {data.length > 10
                                        ? data
                                              .filter((phase: any) => {
                                                  var date: any = moment(
                                                      phase[
                                                          "Voltooiing opdracht"
                                                      ],
                                                      "DD-MM-YYYY"
                                                  );
                                                  var dateNow: any = moment();
                                                  if (
                                                      phase[
                                                          "Voltooiing opdracht"
                                                      ] &&
                                                      date >= dateNow
                                                  ) {
                                                      return true;
                                                  } else {
                                                      return false;
                                                  }
                                              })
                                              .sort(function (a: any, b: any) {
                                                  var aDate: any = moment(
                                                      a["Voltooiing opdracht"],
                                                      "DD-MM-YYYY"
                                                  );
                                                  var bDate: any = moment(
                                                      b["Voltooiing opdracht"],
                                                      "DD-MM-YYYY"
                                                  );
                                                  
                                                  // If either date is invalid, treat it as older
                                                  if (Number.isNaN(aDate)) return 1;
                                                  if (Number.isNaN(bDate)) return -1;
                                                  
                                                  return aDate - bDate; // Reverse sort (most recent first)
                                              })
                                              .splice(0, 10)
                                              .map(
                                                  (
                                                      contract: any,
                                                      index: any
                                                  ) => (
                                                      <Link
                                                          key={index}
                                                          className="border-t w-full hover:underline cursor-pointer"
                                                          to={
                                                              stripeStatus === 'premium'
                                                                  ? `/tender/821868/${contract["Id publicatie"]}`
                                                                  : ""
                                                          }
                                                      >
                                                          <p
                                                              className="p-3 text-sm font-bold truncate"
                                                              data-te-toggle="tooltip"
                                                              title={
                                                                  contract[
                                                                      "Naam aanbesteding"
                                                                  ]
                                                              }
                                                          >
                                                              {
                                                                  contract[
                                                                      "Naam aanbesteding"
                                                                  ]
                                                              }
                                                          </p>
                                                          <p
                                                              className="px-3 pb-3 text-xs truncate"
                                                              data-te-toggle="tooltip"
                                                              title={
                                                                  contract[
                                                                      "Officiele benaming"
                                                                  ]
                                                              }
                                                          >
                                                              Winnaar:{" "}
                                                              {contract[
                                                                  "Officiele benaming"
                                                              ]
                                                                  ? contract[
                                                                        "Officiele benaming"
                                                                    ]
                                                                  : "Onbekend"}
                                                          </p>
                                                          <p className="px-3 pb-3 text-xs">
                                                              Voltooiingsdatum:{" "}
                                                              {contract[
                                                                  "Voltooiing opdracht"
                                                              ]
                                                                  ? contract[
                                                                        "Voltooiing opdracht"
                                                                    ]
                                                                  : "Onbekend"}
                                                          </p>
                                                      </Link>
                                                  )
                                              )
                                        : data.map(
                                              (contract: any, index: any) => (
                                                  <Link
                                                      key={index}
                                                      className="border-t w-full hover:underline cursor-pointer"
                                                      to={
                                                          stripeStatus === 'premium'
                                                              ? `/tender/821868/${contract["Id publicatie"]}`
                                                              : ""
                                                      }
                                                  >
                                                      <p
                                                          className="p-3 text-sm font-bold truncate"
                                                          data-te-toggle="tooltip"
                                                          title={
                                                              contract[
                                                                  "Naam aanbesteding"
                                                              ]
                                                          }
                                                      >
                                                          {
                                                              contract[
                                                                  "Naam aanbesteding"
                                                              ]
                                                          }
                                                      </p>
                                                      <p
                                                          className="px-3 pb-3 text-xs truncate"
                                                          data-te-toggle="tooltip"
                                                          title={
                                                              contract[
                                                                  "Officiele benaming"
                                                              ]
                                                          }
                                                      >
                                                          Winnaar:{" "}
                                                          {contract[
                                                              "Officiele benaming"
                                                          ]
                                                              ? contract[
                                                                    "Officiele benaming"
                                                                ]
                                                              : "Onbekend"}
                                                      </p>
                                                      <p className="px-3 pb-3 text-xs">
                                                          Voltooiingsdatum:{" "}
                                                          {contract[
                                                              "Voltooiing opdracht"
                                                          ]
                                                              ? contract[
                                                                    "Voltooiing opdracht"
                                                                ]
                                                              : "Onbekend"}
                                                      </p>
                                                  </Link>
                                              )
                                          )}
                                </div>
                                {stripeStatus !== 'premium' && (
                                    <div className="absolute w-full h-full flex items-center justify-center">
                                        <FontAwesomeIcon
                                            className="mr-3 drop-shadow-xl animate-bounce"
                                            color="#ffd700"
                                            icon="crown"
                                        />
                                        <p className="text-primary-pink drop-shadow-2xl">
                                            Premium
                                        </p>
                                    </div>
                                )}
                            </div>
                            <div className="bg-white shadow border rounded-lg col-span-2 row-span-2 flex flex-col py-5">
                                <p className="opacity-50 text-sm pb-3 pl-3">
                                    Aantal aanbestedingen en aantal
                                    inschrijvingen per jaar
                                </p>
                                <div className="relative w-full pt-2 flex items-center justify-center">
                                    <TendersApplyChart data={data} />
                                </div>
                            </div>
                            <div className="bg-white shadow border rounded-lg col-span-1 row-span-1 flex flex-col py-5">
                                <p className="opacity-50 pl-3 pb-3 text-sm w-full">
                                    Top 20 aantal gewonnen aanbestedingen
                                </p>
                                <div className="relative w-full flex items-center justify-center pt-30">
                                    <WonTendersChart
                                        data={data}
                                        colorPalette={colorPalette}
                                    />
                                </div>
                            </div>
                            <div className="bg-white shadow border rounded-lg col-span-1 row-span-1 flex flex-col py-5">
                                <p className="opacity-50 pl-3 pr-1 pb-3 text-sm w-full">
                                    Top 20 aanbestedingen per aanbestedende
                                    dienst
                                </p>
                                <div className="relative w-full flex items-center justify-center pt-30">
                                    <TendersPublishedChart
                                        data={data}
                                        colorPalette={colorPalette}
                                    />
                                </div>
                            </div>
                            <div className="bg-white shadow border relative rounded-lg row-span-2 flex col-span-1 flex-col py-3">
                                <p className="opacity-50 text-sm pb-3 pl-3">
                                    Top 5 regio's per aanbesteding
                                </p>
                                <div
                                    className={clsx(
                                        "relative w-full flex items-center justify-center",
                                        stripeStatus !== 'premium' && "blur"
                                    )}
                                >
                                    <TendersRegionChart data={data} />
                                </div>
                                {stripeStatus !== 'premium' && (
                                    <div className="absolute w-full h-full flex items-center justify-center">
                                        <FontAwesomeIcon
                                            className="mr-3 drop-shadow-xl animate-bounce"
                                            color="#ffd700"
                                            icon="crown"
                                        />
                                        <p className="text-primary-pink drop-shadow-2xl">
                                            Premium
                                        </p>
                                    </div>
                                )}
                            </div>
                            <div className="bg-white shadow border relative rounded-lg row-span-2 flex col-span-1 flex-col py-3">
                                <p className="opacity-50 text-sm pb-3 pl-3">
                                    Verwachte datum nieuwe uitvraag
                                </p>
                                <div
                                    className={clsx(
                                        "relative w-full flex items-center justify-center",
                                        stripeStatus !== 'premium' && "blur"
                                    )}
                                >
                                    <ExpectedTendersChart data={data} />
                                </div>
                                {stripeStatus !== 'premium' && (
                                    <div className="absolute w-full h-full flex items-center justify-center">
                                        <FontAwesomeIcon
                                            className="mr-3 drop-shadow-xl animate-bounce"
                                            color="#ffd700"
                                            icon="crown"
                                        />
                                        <p className="text-primary-pink drop-shadow-2xl">
                                            Premium
                                        </p>
                                    </div>
                                )}
                            </div>
                            <div className="bg-white shadow border relative rounded-lg row-span-2 flex col-span-1 flex-col py-3">
                                <p className="opacity-50 text-sm pb-3 pl-5">
                                    Top 5 aanbestedingen per CPV
                                </p>
                                <div
                                    className={clsx(
                                        "relative w-full flex items-center justify-center",
                                        stripeStatus !== 'premium' && "blur"
                                    )}
                                >
                                    <CPVChart
                                        data={data}
                                        colorPalette={colorPalette}
                                    />
                                </div>
                                {stripeStatus !== 'premium' && (
                                    <div className="absolute w-full h-full flex items-center justify-center">
                                        <FontAwesomeIcon
                                            className="mr-3 drop-shadow-xl animate-bounce"
                                            color="#ffd700"
                                            icon="crown"
                                        />
                                        <p className="text-primary-pink drop-shadow-2xl">
                                            Premium
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default Competitor;
